// icloud-provider.js — iCloud Calendar via CalDAV.
//
// Thin subclass of CalDavProvider with Apple-specific defaults and quirks:
//   • Server URL preset to https://caldav.icloud.com/
//   • iCloud uses HTTPS-only basic auth with an *app-specific password* —
//     the user generates one at https://appleid.apple.com/account/manage
//     under "Sign-in and Security → App-Specific Passwords". Regular Apple-
//     ID passwords are rejected.
//   • Apple's PROPFIND on the root URL returns the principal directly; many
//     other servers require a follow-up PROPFIND on /principals/. We use
//     the standard CalDavProvider discovery flow — it works against both.
//   • iCloud honours ctag for collection-level change detection (RFC-adjacent
//     calendarserver.org extension) but does NOT support sync-collection,
//     so delta sync stays off (capabilities.delta = false from the base).
//   • PROPPATCH on calendar collections is rejected — we never write the
//     server-side calendar color; user colors are stored locally in
//     settings.calendars.colors instead.

import { ProviderRegistry } from '../provider-registry.js';
import { CalDavProvider } from './caldav-provider.js';
import { CalendarProviderError, ERROR_CODES } from './calendar-provider.js';

export const ICLOUD_BASE = 'https://caldav.icloud.com/';

export class iCloudProvider extends CalDavProvider {
  /** @param {any} [opts] */
  constructor(opts: any) {
    super({
      ...opts,
      id: 'icloud',
      serverUrl: opts?.serverUrl || ICLOUD_BASE,
    });
  }

  /** @returns {Promise<any>} */
  async init() {
    if (!this.credentialKey) {
      throw new CalendarProviderError(
        ERROR_CODES.AUTH_REQUIRED,
        'iCloud requires an app-specific password — generate one at appleid.apple.com'
      );
    }
    return super.init();
  }
}

// Phase 4.7: explicit ESM registration (see caldav-provider.js for why the
// import-driven ordering replaces the old window-timed self-register).
ProviderRegistry.register('icloud', iCloudProvider);

// Runtime seam: calendars.js (settings panel, outside this partition) reads
// the iCloud base URL off window rather than importing it. Keep the mirror.
if (typeof window !== 'undefined') window.ICLOUD_CALDAV_BASE = ICLOUD_BASE;
